import { Button, Divider } from '@heroui/react';
import React, { memo, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faChevronRight, faAdd } from '@fortawesome/free-solid-svg-icons';
import Label from '../components/Label';
import SearchableListbox from '../components/filter-main/Searchable-Listbox';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { AppDispatch } from '../../../core/store';
import { selectFiltersBelongingToUser, selectFiltersSharedWithUser, selectIsFiltersLoading } from '../filterSelectors';
import ModalManager from '../../../shared/components/modal/ModalManager';
import {
	deleteDraftFilterRequest,
	deleteFilterRequest,
	displayOutputByFilterIdRequest,
	getFilterRequest,
	getFiltersRequest,
	setSelectedFilterRequest,
} from '../filterSlice';
import { IFilterForm } from '../filterTypes';
import { addSeascapeLogActivity } from '../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../activity-logging/activityLogTypes';

interface IFilterMainPanelProps {
	title: string;
	displayFilterDetailPanel: (value: boolean, filterId: number | null, tempId?: string, isDraft?: boolean) => void;
	displayFilterMainPanel: (panel: string) => void;
	isDetailOpen: boolean;
	selectedFilter?: IFilterForm | null;
}

const FilterMainPanel: React.FC<IFilterMainPanelProps> = ({
	title,
	displayFilterDetailPanel,
	displayFilterMainPanel,
	isDetailOpen,
	selectedFilter,
}) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const filtersSharedWithUser = useAppSelector(selectFiltersSharedWithUser);
	const filtersBelongingToUser = useAppSelector(selectFiltersBelongingToUser);
	const isLoading = useAppSelector(selectIsFiltersLoading);

	useEffect(() => {
		if (filtersSharedWithUser.length + filtersBelongingToUser.length === 0) {
			dispatch(getFiltersRequest());
		}
	}, [dispatch, filtersSharedWithUser.length, filtersBelongingToUser.length]);

	const selectAndEditFilter = useCallback(
		(item: IFilterForm) => {
			displayFilterDetailPanel(true, item.id, item.tempId, item.isDraft);
		},
		[displayFilterDetailPanel],
	);

	const handleAddNewFilterOnClick = useCallback(() => {
		displayFilterDetailPanel(true, null);
	}, [displayFilterDetailPanel]);

	const deleteFilter = useCallback(
		(item: IFilterForm) => {
			ModalManager.show({
				header: 'Confirmation',
				message: 'Do you want to continue to delete selected filter?',
				onConfirm: () => {
					displayFilterDetailPanel(false, null);
					if (item.id) {
						dispatch(deleteFilterRequest({ id: item.id }));
					} else {
						dispatch(deleteDraftFilterRequest({ tempId: item.tempId ?? '' }));
					}
				},
			});
		},
		[dispatch, displayFilterDetailPanel],
	);

	const handleCloseFilterMainOnClick = useCallback(() => {
		displayFilterMainPanel('');
		dispatch(setSelectedFilterRequest({ id: null }));
	}, [dispatch, displayFilterMainPanel]);

	const clickFilterLink = useCallback(
		(id: number | string) => {
			dispatch(displayOutputByFilterIdRequest({ id: id }));

			dispatch(setSelectedFilterRequest({ id: null }));
			displayFilterMainPanel('');

			const personalFilter = filtersBelongingToUser?.find((f) => f.id === id);

			if (personalFilter === undefined || personalFilter === null) {
				const teamFilter = filtersSharedWithUser?.find((f) => f.id === id);
				const payload = JSON.stringify({ FilterId: id, Name: teamFilter?.name, Scope: 'Team' });
				const eventId = SeascapeActivityLogConstants.FILTER_APPLY;
				addSeascapeLogActivity({ eventId, payload });
			} else {
				const payload = JSON.stringify({ FilterId: id, Name: personalFilter.name, Scope: 'Personal' });
				const eventId = SeascapeActivityLogConstants.FILTER_APPLY;
				addSeascapeLogActivity({ eventId, payload });
			}
		},
		[dispatch, displayFilterMainPanel, filtersBelongingToUser, filtersSharedWithUser],
	);

	return (
		<div className="flex flex-row h-full w-full">
			{/* <div className='flex-1'>
                <Divider className="my-0 bg-gray-200" orientation="vertical" />
            </div> */}
			<div className="flex flex-col w-full">
				<div className="flex-initial px-3 py-1 text-center justify-between">
					<Label title={title} textSize="lg" />
					<span
						className="cursor-pointer float-right mt-0.5"
						onClick={() => (isDetailOpen ? displayFilterDetailPanel(false, null) : handleCloseFilterMainOnClick())}
					>
						{isDetailOpen ? <FontAwesomeIcon icon={faChevronRight} size="sm" /> : <FontAwesomeIcon icon={faClose} size="sm" />}
					</span>
				</div>
				<Divider className="w-auto mx-2 mt-1" />
				<div className="flex flex-col h-full overflow-y-hidden mt-1 mb-2 space-y-2 mx-2">
					<div className="flex-initial flex items-center justify-between">
						<Label title="Personal Filters" textSize="sm" />
						<Button
							size="sm"
							isDisabled={isLoading}
							variant="ghost"
							onPress={handleAddNewFilterOnClick}
							className={`cursor-pointer rounded border ml-4 h-5 font-semibold text-xs text-msi-midblue-color-500 ${selectedFilter?.id === null && isDetailOpen ? 'bg-[#D4D4D8]' : 'bg-transparent'}`}
							endContent={<FontAwesomeIcon icon={faAdd} size="sm" />}
						>
							Add New
						</Button>
					</div>
					<div className="flex flex-col flex-1 overflow-y-hidden w-full max-w-[350px]">
						<SearchableListbox
							key="personal-filter"
							spinnerLabel="Personal filters are loading"
							isLoading={isLoading}
							isDetailPanelOpen={isDetailOpen}
							selectedItem={selectedFilter}
							type="personal"
							clickItemLink={clickFilterLink}
							deleteItem={deleteFilter}
							editItem={selectAndEditFilter}
							refreshItem={(id) => dispatch(getFilterRequest({ id: id }))}
							items={filtersBelongingToUser}
						/>
					</div>
					<Label title="Team Filters" textSize="sm" />
					<div className="flex flex-col flex-1 overflow-y-hidden w-full max-w-[300px]">
						<SearchableListbox
							key="team-filter"
							spinnerLabel="Team filters are loading"
							isLoading={isLoading}
							isDetailPanelOpen={isDetailOpen}
							selectedItem={selectedFilter}
							type="team"
							clickItemLink={clickFilterLink}
							editItem={selectAndEditFilter}
							refreshItem={(id) => dispatch(getFilterRequest({ id: id }))}
							items={filtersSharedWithUser}
							deleteItem={deleteFilter}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default memo(FilterMainPanel);
